import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { cn } from '../../../utils/classNames';
import { useTranslation } from '../../../hooks/useTranslation';

// Industry-leading video component with intersection observer and motion preferences
const LazyVideo: React.FC<{
  src: string;
  className?: string;
  aspectRatio?: string;
}> = ({ src, className = '', aspectRatio = '16/9' }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  
  // Get base filename without extension for multiple format support
  const baseSrc = src.replace(/\.(webm|mp4)$/, '');

  // Check for reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    
    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches);
    };
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Intersection Observer for true lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !shouldLoad) {
          setShouldLoad(true);
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px' // Start loading 50px before entering viewport
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [shouldLoad]);

  // Handle video events
  const handleLoadedData = () => {
    setIsLoaded(true);
  };

  const handleCanPlay = () => {
    if (videoRef.current && !prefersReducedMotion) {
      videoRef.current.play().catch(() => {
        // Autoplay failed, but that's ok
      });
    }
  };

  return (
    <div 
      ref={containerRef}
      className={cn('relative overflow-hidden flex items-center justify-center', className)}
      style={{ aspectRatio }}
    >
      {/* Loading placeholder */}
      <div className={cn(
        'absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 transition-opacity duration-500',
        isLoaded ? 'opacity-0' : 'opacity-100'
      )}>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-12 h-12 rounded-full border-2 border-gray-300 border-t-primary-500 animate-spin" />
        </div>
      </div>

      {/* Video element */}
      {shouldLoad && (
        <video
          ref={videoRef}
          muted
          loop
          playsInline
          preload="metadata"
          autoPlay={!prefersReducedMotion}
          className={cn(
            'absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-500',
            isLoaded ? 'opacity-100' : 'opacity-0'
          )}
          onLoadedData={handleLoadedData}
          onCanPlay={handleCanPlay}
        >
          {/* WebM first for smaller file size (Chrome, Firefox) */}
          <source src={`${baseSrc}.webm`} type="video/webm" />
          {/* MP4 fallback for Safari, iPhone, and other browsers */}
          <source src={`${baseSrc}.mp4`} type="video/mp4" />
        </video>
      )}

      {/* Play button overlay for reduced motion users */}
      {prefersReducedMotion && isLoaded && (
        <button
          onClick={() => {
            if (videoRef.current) {
              if (videoRef.current.paused) {
                videoRef.current.play();
              } else {
                videoRef.current.pause();
              }
            }
          }}
          className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-colors duration-200 group"
          aria-label="Play video"
        >
          <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center group-hover:bg-white transition-colors duration-200">
            <svg 
              className="w-6 h-6 text-gray-800 ml-1" 
              fill="currentColor" 
              viewBox="0 0 24 24"
            >
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </button>
      )}
    </div>
  );
};

const Easy: React.FC = () => {
  const { t } = useTranslation();
  
  return (
    <div
      id="features"
      className="w-full mx-auto bg-white dark:bg-neutral-950 py-20 px-4 md:px-8"
    >
      <Header>
        <h2 className="font-sans text-bold text-xl text-center md:text-4xl w-fit mx-auto font-bold tracking-tight text-neutral-800 dark:text-neutral-100">
          {t('easySection.title')}
        </h2>
      </Header>
      <p className="max-w-lg text-sm text-neutral-600 text-center mx-auto mt-4 dark:text-neutral-400">
        {t('easySection.subtitle')}
      </p>
      <div className="mt-20 grid grid-cols-1 md:grid-cols-5 gap-4 md:auto-rows-[27rem] max-w-7xl mx-auto">
        <Card className="flex flex-col justify-between md:col-span-3">
          <CardSkeletonBody>
            <div className="w-full h-full p-4 rounded-lg bg-neutral-100 border border-neutral-200 dark:bg-neutral-800 dark:border-neutral-700 ml-6 mt-2 flex items-center justify-center overflow-hidden">
              <LazyVideo 
                src="/videos/add_company_name_nopadding"
                className="rounded-xl border border-gray-200 w-[calc(100%-24px)] max-w-[580px] mx-auto"
                aspectRatio="16/10"
              />
            </div>
          </CardSkeletonBody>
          <CardContent className="h-40">
            <CardTitle>{t('easySection.oneClickInvoice.title')}</CardTitle>
            <CardDescription>
              {t('easySection.oneClickInvoice.description')}
            </CardDescription>
          </CardContent>
        </Card>

        <Card className="flex flex-col justify-between md:col-span-2">
          <CardContent className="h-40">
            <CardTitle>{t('easySection.intuitiveWorkflow.title')}</CardTitle>
            <CardDescription>
              {t('easySection.intuitiveWorkflow.description')}
            </CardDescription>
          </CardContent>
          <CardSkeletonBody>
            <div className="w-full h-full p-4 rounded-lg bg-neutral-100 border border-neutral-200 dark:bg-neutral-800 dark:border-neutral-700 ml-6 mt-2">
              <img
                src="/images/invoiceclean.jpg"
                alt="Dashboard"
                className="w-full object-cover rounded-lg"
              />
            </div>
          </CardSkeletonBody>
        </Card>

        <Card className="flex flex-col justify-between md:col-span-2">
          <CardContent className="h-40">
            <CardTitle>{t('easySection.paymentOptions.title')}</CardTitle>
            <CardDescription>
              {t('easySection.paymentOptions.description')}
            </CardDescription>
          </CardContent>
          <CardSkeletonBody>
            <PaymentLogos />
          </CardSkeletonBody>
        </Card>
        <Card className="flex flex-col justify-between md:col-span-3">
          <CardContent className="h-40">
            <CardTitle>{t('easySection.cloudSync.title')}</CardTitle>
            <CardDescription>
              {t('easySection.cloudSync.description')}
            </CardDescription>
          </CardContent>
          <CardSkeletonBody>
            <div className="w-full h-full max-w-[calc(100%-24px)] rounded-lg bg-neutral-100 border border-neutral-200 dark:bg-neutral-800 dark:border-neutral-700 ml-6 mt-2 flex items-center justify-center overflow-hidden">
              <LazyVideo 
                src="/videos/send_invoice_nopadding"
                className="rounded-xl border border-gray-200 w-[calc(100%-24px)] max-w-[580px] mx-auto"
                aspectRatio="16/10"
              />
            </div>
          </CardSkeletonBody>
        </Card>
      </div>
    </div>
  );
};

const Header = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="relative w-fit mx-auto p-4 flex items-center justify-center">
      <motion.div
        initial={{
          width: 0,
          height: 0,
          borderRadius: 0,
        }}
        whileInView={{
          width: "100%",
          height: "100%",
        }}
        style={{
          transformOrigin: "top-left",
        }}
        transition={{
          duration: 1,
          ease: "easeInOut",
        }}
        className="absolute inset-0 h-full border border-neutral-200 dark:border-neutral-800 w-full"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.1, ease: "easeInOut" }}
          className="absolute -top-1 -left-1 h-2 w-2 dark:bg-neutral-800 bg-neutral-200"
        />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.1, ease: "easeInOut" }}
          className="absolute -top-1 -right-1 h-2 w-2 dark:bg-neutral-800 bg-neutral-200"
        />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.1, ease: "easeInOut" }}
          className="absolute -bottom-1 -left-1 h-2 w-2 dark:bg-neutral-800 bg-neutral-200"
        />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.1, ease: "easeInOut" }}
          className="absolute -bottom-1 -right-1 h-2 w-2 dark:bg-neutral-800 bg-neutral-200"
        />
      </motion.div>
      {children}
    </div>
  );
};

// Skeletons
export const SkeletonOne = () => {
  const Container = ({
    children,
    ...props
  }: { children: React.ReactNode } & React.ComponentProps<
    typeof motion.div
  >) => {
    return (
      <motion.div
        {...props}
        className={cn(
          "w-full h-14 md:h-40 p-2 rounded-lg relative shadow-lg flex items-center bg-gradient-to-b from-white to-white dark:from-neutral-800 dark:to-neutral-700 justify-center",
          props.className
        )}
      >
        {children}
      </motion.div>
    );
  };
  return (
    <div className="relative flex items-center justify-center w-full h-full">
      <svg
        width="128"
        height="69"
        viewBox="0 0 128 69"
        fill="none"
        className="absolute left-1/2 -translate-x-[90%] -top-2 text-neutral-200 dark:text-neutral-800"
      >
        <path
          d="M1.00002 0.5L1.00001 29.5862C1 36.2136 6.37259 41.5862 13 41.5862H115C121.627 41.5862 127 46.9588 127 53.5862L127 75"
          stroke="currentColor"
          strokeWidth="1"
        />
        <motion.path
          d="M1.00002 0.5L1.00001 29.5862C1 36.2136 6.37259 41.5862 13 41.5862H115C121.627 41.5862 127 46.9588 127 53.5862L127 75"
          stroke="url(#gradient-2)"
          strokeWidth="1"
        />

        <defs>
          <motion.linearGradient
            initial={{
              x1: "0%",
              y1: "0%",
              x2: "0%",
              y2: "0%",
            }}
            animate={{
              x1: "100%",
              y1: "90%",
              x2: "120%",
              y2: "120%",
            }}
            id="gradient-2"
            transition={{
              duration: Math.random() * (7 - 2) + 2,
              ease: "linear",
              repeat: Infinity,
            }}
          >
            <stop stopColor="#001AFF" stopOpacity={`0`} />
            <stop offset="1" stopColor="#6DD4F5" />
            <stop offset="1" stopColor="#6DD4F5" stopOpacity="0" />
          </motion.linearGradient>
        </defs>
      </svg>

      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="62"
        height="105"
        viewBox="0 0 62 105"
        fill="none"
        className="absolute left-1/2 -translate-x-0 -bottom-2 dark:text-neutral-800 text-neutral-200"
      >
        <path
          d="M1.00001 -69L1 57.5C1 64.1274 6.37258 69.5 13 69.5H49C55.6274 69.5 61 74.8726 61 81.5L61 105"
          stroke="currentColor"
          strokeWidth="1"
        />
        <motion.path
          d="M1.00001 -69L1 57.5C1 64.1274 6.37258 69.5 13 69.5H49C55.6274 69.5 61 74.8726 61 81.5L61 105"
          stroke="url(#gradient-1)"
          strokeWidth="1"
        />
        <defs>
          <motion.linearGradient
            initial={{
              x1: "0%",
              y1: "0%",
              x2: "0%",
              y2: "0%",
            }}
            animate={{
              x1: "100%",
              y1: "90%",
              x2: "120%",
              y2: "120%",
            }}
            id="gradient-1"
            transition={{
              duration: Math.random() * (7 - 2) + 2,
              ease: "linear",
              repeat: Infinity,
            }}
          >
            <stop stopColor="#001AFF" stopOpacity={`0`} />
            <stop offset="1" stopColor="#6DD4F5" />
            <stop offset="1" stopColor="#6DD4F5" stopOpacity="0" />
          </motion.linearGradient>
        </defs>
      </svg>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-lg mx-auto w-full relative z-30 [perspective:1000px] [transform-style:preserve-3d] p-8 sm:p-0">
        <Container
          initial={{ y: 0 }}
          animate={{ y: [0, -10, 0], rotateX: [0, 10, 0] }}
          transition={{
            duration: 2,
            ease: "easeInOut",
            repeat: Infinity,
            repeatDelay: 6,
          }}
          className="overflow-hidden px-2 flex-col justify-center font-mono items-start text-neutral-800 dark:text-neutral-300"
        >
          <p className="text-[8px] bg-transparent">Create invoice</p>
          <p className="text-[8px] bg-transparent">Add line items</p>
          <p className="text-[8px] bg-transparent">Send to client</p>
        </Container>
        <Container
          initial={{ y: 0 }}
          animate={{ y: [0, -10, 0], rotateX: [0, 10, 0] }}
          transition={{
            duration: 2,
            delay: 2,
            ease: "easeInOut",
            repeat: Infinity,
            repeatDelay: 6,
          }}
        >
          <InvoiceIcon />
        </Container>
        <Container
          initial={{ y: 0 }}
          animate={{ y: [0, -10, 0], rotateX: [0, 10, 0] }}
          transition={{
            duration: 2,
            delay: 4,
            ease: "easeInOut",
            repeat: Infinity,
            repeatDelay: 6,
          }}
          className="flex flex-col justify-center items-center"
        >
          <CheckIcon />
          <p className="text-[8px] bg-transparent">payment received ✨</p>
        </Container>
      </div>
    </div>
  );
};

export const SkeletonTwo = () => {
  return (
    <div className="h-60 md:h-60 flex flex-col items-center relative bg-transparent dark:bg-transparent mt-10">
      <Globe />
    </div>
  );
};

export const Globe = ({ className }: { className?: string }) => {
  return (
    <div className={cn("relative w-full h-full flex items-center justify-center", className)}>
      <div className="absolute w-full h-full rounded-full bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-900/10 animate-pulse" />
      <div className="relative w-3/4 h-3/4 rounded-full overflow-hidden border border-blue-100 dark:border-blue-800">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 via-transparent to-transparent" />
        <div className="absolute top-1/4 left-1/4 w-2 h-2 rounded-full bg-blue-500 shadow-lg shadow-blue-500/50" />
        <div className="absolute top-1/3 right-1/3 w-1.5 h-1.5 rounded-full bg-blue-400 shadow-lg shadow-blue-400/50" />
        <div className="absolute bottom-1/4 right-1/4 w-2 h-2 rounded-full bg-blue-500 shadow-lg shadow-blue-500/50" />
      </div>
    </div>
  );
};

// Icons
const InvoiceIcon = () => {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-8 w-8 object-contain text-black dark:text-white"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </svg>
  );
};

const CheckIcon = () => {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-8 w-8 object-contain text-green-500 dark:text-green-400"
    >
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
};

// Card structure
const CardSkeletonBody = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div className={cn("overflow-hidden relative w-full h-full", className)}>
      {children}
    </div>
  );
};

const CardContent = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return <div className={cn("p-6", className)}>{children}</div>;
};

const CardTitle = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <h3
      className={cn(
        "font-sans text-base font-medium tracking-tight text-neutral-700 dark:text-neutral-100",
        className
      )}
    >
      {children}
    </h3>
  );
};

const CardDescription = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <p
      className={cn(
        "font-sans max-w-xs text-base font-normal tracking-tight mt-2 text-neutral-500 dark:text-neutral-400",
        className
      )}
    >
      {children}
    </p>
  );
};

const Card = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <motion.div
      whileHover="animate"
      className={cn(
        "group isolate flex flex-col rounded-2xl bg-white dark:bg-neutral-900 shadow-[0_1px_1px_rgba(0,0,0,0.05),0_4px_6px_rgba(34,42,53,0.04),0_24px_68px_rgba(47,48,55,0.05),0_2px_3px_rgba(0,0,0,0.04)] overflow-hidden",
        className
      )}
    >
      {children}
    </motion.div>
  );
};

// Payment Logos Component
const PaymentLogos = () => {
  const paymentLogos = [
    { src: "/images/payment-logos/visa.svg", alt: "Visa", className: "scale-110" },
    { src: "/images/payment-logos/mastercard.svg", alt: "Mastercard", className: "scale-110" },
    { src: "/images/payment-logos/american-express.svg", alt: "American Express", className: "scale-90" },
    { src: "/images/payment-logos/apple-pay.svg", alt: "Apple Pay", className: "scale-110" },
    { src: "/images/payment-logos/google-pay.svg", alt: "Google Pay", className: "scale-100" },
    { src: "/images/payment-logos/paypal.svg", alt: "PayPal", className: "scale-90" },
    { src: "/images/payment-logos/sepa.svg", alt: "SEPA", className: "scale-90" },
    { src: "/images/payment-logos/ideal.svg", alt: "iDeal", className: "scale-100" },
    { src: "/images/payment-logos/klarna.svg", alt: "Klarna", className: "scale-100" }
  ];

  return (
    <div className="relative w-full h-full overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-neutral-50 to-neutral-100 dark:from-neutral-800 dark:to-neutral-900 rounded-lg"></div>
      
      {/* Floating logos with staggered animation */}
      <div className="relative w-full h-full p-4 grid grid-cols-3 gap-2">
        {paymentLogos.map((logo, index) => (
          <motion.div
            key={logo.alt}
            className="flex items-center justify-center p-2 overflow-visible"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ 
              duration: 0.5, 
              delay: index * 0.1,
              ease: "easeOut"
            }}
            whileHover={{ 
              scale: 1.05, 
              rotate: [-1, 1, -1, 0],
              transition: { duration: 0.3 } 
            }}
          >
            <div className="relative bg-white dark:bg-neutral-800 rounded-lg shadow-sm p-3 w-full h-full flex items-center justify-center overflow-hidden border border-neutral-200 dark:border-neutral-700">
              <img 
                src={logo.src} 
                alt={logo.alt} 
                className={cn(
                  "h-8 w-auto object-contain filter drop-shadow-sm transition-all duration-300",
                  logo.className
                )}
              />
              
              {/* Subtle highlight effect */}
              <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
            </div>
          </motion.div>
        ))}
      </div>
      
      {/* Decorative elements */}
      <div className="absolute bottom-2 right-2 w-20 h-20 rounded-full bg-gradient-to-tr from-primary-100/20 to-transparent dark:from-primary-900/20 blur-xl"></div>
      <div className="absolute top-4 left-4 w-12 h-12 rounded-full bg-gradient-to-bl from-blue-100/20 to-transparent dark:from-blue-900/20 blur-lg"></div>
    </div>
  );
};

export default Easy; 